import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";

interface SubscriptionStatus {
  subscribed: boolean;
  planType: string | null;
  seatLimit: number;
  subscriptionEnd: string | null;
  organizationId: string | null;
}

const SUBSCRIPTION_CACHE_KEY = 'sorvaine_subscription_status';
const LEGACY_CACHE_KEY = 'corvence_subscription_status';
// Clean up legacy key on load
try { sessionStorage.removeItem(LEGACY_CACHE_KEY); } catch { /* noop */ }
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

function getCachedSubscription(): SubscriptionStatus | null {
  try {
    const cached = sessionStorage.getItem(SUBSCRIPTION_CACHE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (parsed.timestamp && Date.now() - parsed.timestamp < CACHE_TTL_MS) {
        return parsed.data;
      }
    }
  } catch {
    // Ignore errors
  }
  return null;
}

function setCachedSubscription(data: SubscriptionStatus) {
  try {
    sessionStorage.setItem(SUBSCRIPTION_CACHE_KEY, JSON.stringify({
      data,
      timestamp: Date.now()
    }));
  } catch {
    // Ignore errors
  }
}

export function useSubscription() {
  const { user, session, loading: authLoading } = useAuth();

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ["subscription", user?.id],
    queryFn: async (): Promise<SubscriptionStatus> => {
      const { data, error } = await supabase.functions.invoke("check-subscription");

      const defaultUnsubscribed: SubscriptionStatus = {
        subscribed: false,
        planType: null,
        seatLimit: 1,
        subscriptionEnd: null,
        organizationId: null,
      };

      // If the backend responds non-2xx, supabase-js surfaces it as `error`.
      if (error) {
        console.error("Error checking subscription:", error);
        // Fall back to cache if available
        const cached = getCachedSubscription();
        if (cached?.subscribed) {
          console.warn("Subscription check failed but using cached subscribed state");
          return cached;
        }
        return defaultUnsubscribed;
      }

      const result: SubscriptionStatus = {
        subscribed: data?.subscribed ?? false,
        planType: data?.planType ?? null,
        seatLimit: data?.seatLimit ?? 1,
        subscriptionEnd: data?.subscriptionEnd ?? null,
        organizationId: data?.organizationId ?? null,
      };

      // Cache successful subscription status

      if (result.subscribed) {
        setCachedSubscription(result);
      }

      return result;
    },
    enabled: !authLoading && !!user && !!session?.access_token,
    staleTime: 60 * 1000,
    refetchInterval: 300 * 1000,
    refetchOnWindowFocus: true,
    // Use cached data as placeholder so background refetches never show a loading spinner
    placeholderData: (previousData: SubscriptionStatus | undefined) => {
      if (previousData) return previousData;
      const cached = getCachedSubscription();
      return cached ?? undefined;
    },
  });

  return {
    isLoading,
    isSubscribed: data?.subscribed ?? false,
    planType: data?.planType ?? null,
    seatLimit: data?.seatLimit ?? 1,
    subscriptionEnd: data?.subscriptionEnd ?? null,
    organizationId: data?.organizationId ?? null,
    error,
    refetch,
  };
}
